import { useState } from 'react'
import { api, fieldErrorsFromDetail } from '../lib/api'

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-CA', {
    timeZone: 'America/Toronto',
    dateStyle: 'medium',
  })
}

const RATING_TEXT = { up: 'rated thumbs up', down: 'rated thumbs down' }

// Name one staff member: who they are and the answers to the space's template
// variables. One call creates the participant AND their draft link
// (POST /spaces/{id}/staff — the template and the expiry are the space's, so
// nothing is chosen here), and the composer opens on that draft.
//
// `initial` prefills the person for a new link to someone already on the
// roster; `activeLinks` lists their live links so a second one is a deliberate
// choice. The duplicate-email check warns (never blocks) when the address has
// been sent a link before, anywhere. `prefill` — {key: value} from
// lib/variables.variablePrefill: the space's default for each variable, else
// its engagement — is what every variable field opens holding, so an answer
// that is the same for everyone costs no typing; it is editable like any
// other answer, and a person's own earlier answers (`initial`) win over it.
export default function AddStaffForm({
  spaceId,
  variables = [],
  prefill = {},
  initial = null,
  activeLinks = [],
  onCreated,
  onCancel,
  onPasteList = null,
}) {
  const [firstName, setFirstName] = useState(initial?.first_name ?? '')
  const [lastName, setLastName] = useState(initial?.last_name ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [values, setValues] = useState(() => {
    const start = {}
    for (const variable of variables) start[variable.key] = prefill[variable.key] ?? ''
    return { ...start, ...(initial?.variables ?? {}) }
  })
  const [fieldErrors, setFieldErrors] = useState({})
  const [serverError, setServerError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [priorLinks, setPriorLinks] = useState(null)

  async function checkEmail() {
    const value = email.trim().toLowerCase()
    if (!EMAIL_RE.test(value)) {
      setPriorLinks(null)
      return
    }
    try {
      const result = await api(
        `/api/v1/spaces/${spaceId}/staff/email-check?email=${encodeURIComponent(value)}`,
      )
      setPriorLinks(result.prior_links)
    } catch {
      // Best-effort warning only — never let the check block data entry.
      setPriorLinks(null)
    }
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (submitting) return

    const errors = {}
    if (!firstName.trim()) errors.first_name = 'First name is required.'
    if (!lastName.trim()) errors.last_name = 'Last name is required.'
    if (!email.trim()) errors.email = 'Email is required.'
    else if (!EMAIL_RE.test(email.trim().toLowerCase()))
      errors.email = 'Enter a valid email address.'
    for (const variable of variables) {
      if (variable.required && !(values[variable.key] || '').trim()) {
        errors[variable.key] = `${variable.label} is required.`
      }
    }
    setFieldErrors(errors)
    setServerError(null)
    if (Object.keys(errors).length > 0) return

    setSubmitting(true)
    try {
      const draft = await api(`/api/v1/spaces/${spaceId}/staff`, {
        method: 'POST',
        body: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
          variables: values,
        },
      })
      onCreated(draft)
    } catch (err) {
      const mapped = fieldErrorsFromDetail(err.detail)
      if (mapped) setFieldErrors(mapped)
      else setServerError(err.detail || 'Could not add them — please try again.')
      setSubmitting(false)
    }
  }

  const title = initial
    ? `New link for ${initial.first_name} ${initial.last_name}`
    : 'Add staff'

  return (
    <div className="card card--elevated" style={{ marginBottom: 'var(--space-m)' }}>
      <div
        className="flex items-baseline justify-between flex-wrap"
        style={{ gap: 'var(--space-xs)', marginBottom: 'var(--space-s)' }}
      >
        <h3 className="text-h5">{title}</h3>
        {/* Several at once: the pasted-list form, for a name-and-email list
            copied from anywhere. */}
        {onPasteList && !initial && (
          <button type="button" className="text-btn" onClick={onPasteList}>
            Paste a list
          </button>
        )}
      </div>

      {serverError && (
        <div
          className="alert alert--error text-body-sm"
          style={{ marginBottom: 'var(--space-s)' }}
          role="alert"
        >
          {serverError}
        </div>
      )}

      {activeLinks.length > 0 && (
        <div className="alert alert--warning" style={{ marginBottom: 'var(--space-s)' }} role="status">
          <div>
            <p className="text-body-sm" style={{ fontWeight: 600 }}>
              They already have a live link in this space
            </p>
            <ul className="text-body-sm" style={{ marginTop: 'var(--space-2xs)' }}>
              {activeLinks.map((active, index) => (
                <li key={index}>{active.label}</li>
              ))}
            </ul>
            <p
              className="text-caption"
              style={{ marginTop: 'var(--space-2xs)', color: 'var(--color-text-secondary)' }}
            >
              Sending another is allowed — each link is separate.
            </p>
          </div>
        </div>
      )}

      {priorLinks && priorLinks.length > 0 && (
        <div className="alert alert--warning" style={{ marginBottom: 'var(--space-s)' }} role="status">
          <div>
            <p className="text-body-sm" style={{ fontWeight: 600 }}>
              This email has been sent a link before
            </p>
            <ul className="text-body-sm" style={{ marginTop: 'var(--space-2xs)' }}>
              {priorLinks.map((prior, index) => (
                <li key={index}>
                  {prior.template_name} — {formatDate(prior.sent_at)} ·{' '}
                  {RATING_TEXT[prior.rating] ?? 'not rated'}
                </li>
              ))}
            </ul>
            <p
              className="text-caption"
              style={{ marginTop: 'var(--space-2xs)', color: 'var(--color-text-secondary)' }}
            >
              You can still add them — this is only a check against accidental duplicates.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="flex flex-col" style={{ gap: 'var(--space-s)' }}>
        <div className="flex flex-col md:flex-row" style={{ gap: 'var(--space-s)' }}>
          <div className="field w-full">
            <label className="field__label" htmlFor="staff-first-name">
              First name
            </label>
            <input
              id="staff-first-name"
              type="text"
              autoComplete="off"
              className={`field__input${fieldErrors.first_name ? ' field__input--invalid' : ''}`}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            {fieldErrors.first_name && <p className="field__error">{fieldErrors.first_name}</p>}
          </div>
          <div className="field w-full">
            <label className="field__label" htmlFor="staff-last-name">
              Last name
            </label>
            <input
              id="staff-last-name"
              type="text"
              autoComplete="off"
              className={`field__input${fieldErrors.last_name ? ' field__input--invalid' : ''}`}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            {fieldErrors.last_name && <p className="field__error">{fieldErrors.last_name}</p>}
          </div>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="staff-email">
            Email
          </label>
          <input
            id="staff-email"
            type="email"
            autoComplete="off"
            className={`field__input${fieldErrors.email ? ' field__input--invalid' : ''}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={checkEmail}
          />
          {fieldErrors.email && <p className="field__error">{fieldErrors.email}</p>}
        </div>

        {/* One field per variable the space's template declares. A template
            with none renders nothing here — that is a valid template. */}
        {variables.map((variable) => (
          <div className="field" key={variable.key}>
            <label className="field__label" htmlFor={`staff-var-${variable.key}`}>
              {variable.label}
              {!variable.required && (
                <span style={{ color: 'var(--color-text-tertiary)' }}> (optional)</span>
              )}
            </label>
            <input
              id={`staff-var-${variable.key}`}
              type="text"
              className={`field__input${fieldErrors[variable.key] ? ' field__input--invalid' : ''}`}
              value={values[variable.key] || ''}
              onChange={(e) =>
                setValues((current) => ({ ...current, [variable.key]: e.target.value }))
              }
            />
            {variable.help && (
              <p className="text-caption" style={{ color: 'var(--color-text-tertiary)' }}>
                {variable.help}
              </p>
            )}
            {fieldErrors[variable.key] && (
              <p className="field__error">{fieldErrors[variable.key]}</p>
            )}
          </div>
        ))}

        <p className="text-body-sm" style={{ color: 'var(--color-text-secondary)' }}>
          This creates a draft invitation you can review before it goes out. It is sent from the
          space&rsquo;s sender name, copied to the principal, and expires with the space.
        </p>

        <div className="flex" style={{ gap: 'var(--space-xs)' }}>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create draft'}
          </button>
          <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
